import React from 'react';
import {
  Dashboard as DashboardIcon,
  ChildCare as ChildIcon,
  FolderSpecial as DocsIcon,
  NoteAlt as CareNotesIcon,
  Today as DailyUpdateIcon,
  EventNote as MeetingsIcon,
  CalendarMonth as CalendarIcon,
  QuestionAnswer as QueriesIcon,
} from '@mui/icons-material';
import HelpGuideLayout from '../../components/HelpGuideLayout';

const sections = [
  {
    id: 'dashboard',
    icon: DashboardIcon,
    title: 'Dashboard',
    content: [
      {
        heading: 'What you see',
        points: [
          'A summary card per child with quick attendance and pending-fees badges.',
          'Upcoming parent meetings and a quick link into the school calendar.',
          'A Daycare panel and Care Notes shortcut appear automatically if any of your children are enrolled in daycare.',
        ],
      },
    ],
  },
  {
    id: 'children',
    icon: ChildIcon,
    title: 'My Children',
    content: [
      {
        heading: 'Viewing your child\'s records',
        points: [
          'Open a child\'s card to see their Attendance, Marks and Fees in separate tabs.',
          'This portal is read-only for academic records — your child\'s teacher and Principal enter attendance, marks and fees; you can\'t edit them.',
        ],
      },
    ],
  },
  {
    id: 'documents',
    icon: DocsIcon,
    title: 'Documents',
    featureKey: 'student_documents',
    content: [
      {
        heading: 'Uploading onboarding documents',
        points: [
          'From a child\'s Documents page, upload required files (birth certificate, transfer certificate, Aadhaar, photo, report card, etc.) as PDF, JPG or PNG.',
          'Limits: 5 MB per file, and a maximum of 5 documents per child.',
          'Once uploaded, you can view and download a document, but you cannot delete it yourself — contact the school if a file needs to be removed.',
          'The school may mark a document Verified or Rejected; check back on the same page for status.',
        ],
      },
    ],
  },
  {
    id: 'care-notes',
    icon: CareNotesIcon,
    title: 'Care Notes',
    featureKey: 'care_notes',
    content: [
      {
        heading: 'Daily instructions for daycare',
        points: [
          'Only available for children actively enrolled in daycare — it won\'t appear for children who aren\'t.',
          'Send the daycare teacher reminders for the day: medication, dietary needs, pickup changes, etc., with a priority level (normal/high/urgent).',
          'You can edit or delete your note only while it\'s still "Pending" — once the teacher acknowledges or completes it, it\'s locked.',
          'You\'ll get an email and in-app notification once the teacher marks your note Completed.',
        ],
      },
    ],
  },
  {
    id: 'daily-updates',
    icon: DailyUpdateIcon,
    title: 'Daily Updates',
    featureKey: 'daily_updates',
    content: [
      {
        heading: 'Your child\'s day, from their teacher',
        points: [
          'View the daily diary entries your child\'s teacher posts — meals, nap, mood and activities.',
          'You\'ll get an in-app notification each time a new update is posted.',
        ],
      },
    ],
  },
  {
    id: 'meetings',
    icon: MeetingsIcon,
    title: 'Parent Meetings',
    content: [
      {
        heading: 'Meetings with teachers/Principal',
        points: [
          'View meetings scheduled by your child\'s teacher or Principal, including the agenda and any notes once completed.',
          'If a meeting is rescheduled or cancelled, you\'ll be emailed automatically.',
        ],
      },
    ],
  },
  {
    id: 'calendar',
    icon: CalendarIcon,
    title: 'School Calendar',
    content: [
      {
        heading: 'Staying on top of school dates',
        points: [
          'See school events, holidays and exam dates in one combined calendar.',
        ],
      },
    ],
  },
  {
    id: 'queries',
    icon: QueriesIcon,
    title: 'Ask a Question',
    featureKey: 'parent_queries',
    content: [
      {
        heading: 'Raising a query with the school',
        points: [
          'My Queries lets you raise a question or request to the school about your child.',
          'Track its status: Open → In Progress → Completed. You\'ll get an email, WhatsApp message and in-app notification once it\'s resolved, including the school\'s resolution note.',
        ],
      },
    ],
  },
];

const ParentHelpGuide = () => (
  <HelpGuideLayout
    title="How to Use ClassTree — Parent Guide"
    subtitle="A quick reference for everything you can do in the Parent Portal. Sections that don't apply to your child are hidden automatically."
    sections={sections}
  />
);

export default ParentHelpGuide;
